import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useCallback } from 'react';

import { useApiClient } from '@@common';

import { AddEnumItemModel, DataSchemaClient, EnumItemModel, NewEnumModel } from '../../../api/api';

type UseEnumControllerOptions = {
	enabled?: boolean;
};

export const useEnumController = (contextId: string, options: UseEnumControllerOptions = {}) => {
	const client = useApiClient(DataSchemaClient);
	const isEnumsQueryEnabled = options.enabled ?? true;

	// Fetch enums with React Query - automatic caching
	const {
		data: enums = [],
		isLoading: loadingEnums,
		refetch,
	} = useQuery({
		queryKey: ['enums', contextId],
		queryFn: () => client.getEnums(contextId),
		enabled: !!contextId && isEnumsQueryEnabled,
		staleTime: 60000, // Cache for 1 minute
		gcTime: 5 * 60 * 1000,
	});

	const mutations = useEnumMutations(contextId);

	return {
		enums,
		loadingEnums,
		refreshEnums: async () => {
			await refetch();
		},
		...mutations,
	};
};

export const useEnumMutations = (contextId: string) => {
	const client = useApiClient(DataSchemaClient);
	const queryClient = useQueryClient();

	const createEnum = useCallback(
		async (name: string, description: string) => {
			const newEnum: NewEnumModel = { name, description, metadata: '' };
			await client.addNewEnum(contextId!, newEnum);
			// Invalidate enums cache to trigger refetch
			queryClient.invalidateQueries({ queryKey: ['enums', contextId] });
			queryClient.invalidateQueries({ queryKey: ['domainModel', contextId] });
		},
		[client, contextId, queryClient],
	);

	const deleteEnum = useCallback(
		async (enumId: string) => {
			await client.deleteEnum(contextId, enumId);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ['enums', contextId] }),
				queryClient.invalidateQueries({ queryKey: ['domainModel', contextId] }),
			]);
		},
		[client, contextId, queryClient],
	);

	const addEnumItem = useCallback(
		async (enumName: string, itemName: string) => {
			if (!enumName || !itemName) return;

			const newItem: AddEnumItemModel = {
				name: itemName,
			};

			await client.addNewEnumItem(contextId!, enumName, newItem);
		},
		[client, contextId],
	);

	const getEnumItems = useCallback(
		async (enumName: string): Promise<EnumItemModel[]> => {
			try {
				const result = await client.getEnumItems(contextId!, enumName);
				return result ?? [];
			} catch (err) {
				console.error('Failed to fetch enum items', err);
				return [];
			}
		},
		[client, contextId],
	);

	const getEnumById = useCallback(
		async (enumId: string) => {
			const enumModel = await client.getEnumById(contextId, enumId);
			if (!enumModel) return null;
			return enumModel;
		},
		[client, contextId],
	);

	return {
		createEnum,
		deleteEnum,
		addEnumItem,
		getEnumItems,
		getEnumById,
	};
};
